<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { usePendenciasStore } from '@/stores/pendencias'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import { formatarDataBR } from '@/utils/useDataBR'

const emit = defineEmits(['show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const erro = ref(null)

const sortField = ref('')
const sortOrder = ref('')

const colunas = [
  { field: 'idOrdemCompra', label: '#', width: 80, sortable: true, slotName: 'idOrdemCompra' },
  { field: 'numeroOrdem', label: 'Nº Ordem', sortable: true, class: 'fw-medium text-body-emphasis' },
  { field: 'nomeFornecedor', label: 'Fornecedor', sortable: false },
  { field: 'dataEmissao', label: 'Emissão', align: 'center', sortable: true, slotName: 'dataEmissao' },
  { field: 'valorTotalNegociado', label: 'Valor Total', align: 'right', class: 'fw-medium', sortable: true, formatter: (v) => formatarValor(v) },
  { field: 'nomeGrupoAprovador', label: 'Grupo', sortable: false },
  { field: 'designadoDiretamente', label: 'Designado', align: 'center', slotName: 'designadoDiretamente', sortable: false },
  { field: 'resumoAprovacao', label: 'Resumo', sortable: false, slotName: 'resumoAprovacao' },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const filtros = reactive({
  buscaNumero: '',
  buscaResumo: '',
  idGrupoAprovador: null,
  faixaValor: ''
})

const listas = reactive({})

// Grupos de aprovadores extraídos da lista carregada
const gruposDisponiveis = computed(() => {
  const map = new Map()
  lista.value.forEach(item => {
    if (item.idGrupoAcessoAprovador && item.nomeGrupoAprovador) {
      map.set(item.idGrupoAcessoAprovador, item.nomeGrupoAprovador)
    }
  })
  return Array.from(map, ([id, nome]) => ({ id, nome }))
})

const faixasValor = [
  { value: 'ate10mil', label: 'Até R$ 10 mil' },
  { value: 'de10a30mil', label: 'R$ 10 a 30 mil' },
  { value: 'acima30mil', label: '> R$ 30 mil' }
]

const temFiltroAtivo = computed(() => {
  return filtros.buscaNumero || filtros.buscaResumo || filtros.idGrupoAprovador || filtros.faixaValor
})

// --- Modal de análise ---
const modalAberto = ref(false)
const ordemDetalhes = ref(null)
const isLoadingDetalhes = ref(false)
const visaoCompleta = ref(false)
const reprovarModalRef = ref(null)
const aprovarModalRef = ref(null)
const reprovarObservacao = ref('')
const aprovarObservacao = ref('')
const idOrdemAnalise = ref(null)
const indiceAtual = ref(-1)
const estaProcessandoAcao = ref(false)
const confirmacaoAtiva = ref(true)

const totalPendentes = computed(() => lista.value.length)
const posicaoLabel = computed(() => {
  if (indiceAtual.value < 0 || totalPendentes.value === 0) return ''
  return `${indiceAtual.value + 1} de ${totalPendentes.value}`
})
const temProxima = computed(() => indiceAtual.value < totalPendentes.value - 1)
const temAnterior = computed(() => indiceAtual.value > 0)

const handleSortChange = () => {
  buscarDados(1)
}

const formatarValor = (valor) => {
  if (valor == null) return '-'
  try {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor)
  } catch (e) {
    return valor?.toFixed(2) ?? '-'
  }
}

const getTextoLista = (lista, id) => {
  if (!id || !lista) return id || '-'
  const idStr = String(id)
  const item = lista.find(i => String(i.idListaClienteItem) === idStr)
  return item ? item.textoLista : idStr
}

const obterSiglaMoeda = (codigoMoeda) => {
  if (!codigoMoeda) return 'BRL'
  const idStr = String(codigoMoeda)
  const itemLista = (listas.LstMoedaPadrao || []).find(m => String(m.idListaClienteItem) === idStr)
  const texto = itemLista ? itemLista.textoLista : codigoMoeda
  const match = String(texto).match(/^[A-Z]{3}/)
  return match ? match[0] : codigoMoeda
}

const formatarMoedaComSigla = (valor, codigoMoeda) => {
  const sigla = obterSiglaMoeda(codigoMoeda)
  return `${formatarValor(valor)} ${sigla}`
}

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['OrdemCompra', 'OrdemCompraItem'])
    Object.assign(listas, result)
  } catch (e) {
    console.warn('Erro ao carregar listas:', e)
  }
}

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
  }
})

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    // Filtros inline
    if (filtros.buscaNumero) params.append('buscaNumero', filtros.buscaNumero)
    if (filtros.buscaResumo) params.append('buscaResumo', filtros.buscaResumo)
    if (filtros.idGrupoAprovador) params.append('idGrupoAprovador', filtros.idGrupoAprovador)
    if (filtros.faixaValor) params.append('faixaValor', filtros.faixaValor)

    const response = await api.get(`/OrdensCompra/aprovacoes?${params.toString()}`)
    lista.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar as aprovações pendentes.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const limparFiltros = () => {
  filtros.buscaNumero = ''
  filtros.buscaResumo = ''
  filtros.idGrupoAprovador = null
  filtros.faixaValor = ''
  buscarDados(1)
}

const alternarFaixaValor = (valor) => {
  filtros.faixaValor = filtros.faixaValor === valor ? '' : valor
  aplicarFiltros()
}

const alternarGrupo = (id) => {
  filtros.idGrupoAprovador = filtros.idGrupoAprovador === id ? null : id
  aplicarFiltros()
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

// --- Navegação entre OCs no modal ---
const carregarOCPorIndice = async (novoIndice) => {
  if (novoIndice < 0 || novoIndice >= totalPendentes.value) return

  const item = lista.value[novoIndice]
  if (!item) return

  indiceAtual.value = novoIndice
  idOrdemAnalise.value = item.idOrdemCompra
  visaoCompleta.value = false
  isLoadingDetalhes.value = true
  ordemDetalhes.value = null

  try {
    const response = await api.get(`/OrdensCompra/${item.idOrdemCompra}`)
    ordemDetalhes.value = response.data
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar detalhes da ordem de compra.', type: 'error' })
    modalAberto.value = false
  } finally {
    isLoadingDetalhes.value = false
  }
}

const abrirModalAnalise = async (item) => {
  const idx = lista.value.findIndex(i => i.idOrdemCompra === item.idOrdemCompra)
  if (idx >= 0) {
    await carregarOCPorIndice(idx)
  } else {
    // Item não está na lista (ex: veio de notificação antes do carregamento)
    indiceAtual.value = -1
    idOrdemAnalise.value = item.idOrdemCompra
    visaoCompleta.value = false
    isLoadingDetalhes.value = true
    ordemDetalhes.value = null
    try {
      const response = await api.get(`/OrdensCompra/${item.idOrdemCompra}`)
      ordemDetalhes.value = response.data
    } catch (error) {
      emit('show-notification', { message: 'Erro ao carregar detalhes da ordem de compra.', type: 'error' })
      modalAberto.value = false
    } finally {
      isLoadingDetalhes.value = false
    }
  }
  modalAberto.value = true
}

const proximaOC = () => {
  if (temProxima.value) {
    carregarOCPorIndice(indiceAtual.value + 1)
  }
}

const anteriorOC = () => {
  if (temAnterior.value) {
    carregarOCPorIndice(indiceAtual.value - 1)
  }
}

const fecharModal = () => {
  modalAberto.value = false
  ordemDetalhes.value = null
  reprovarObservacao.value = ''
  aprovarObservacao.value = ''
  indiceAtual.value = -1
}

const alternarVisao = () => {
  visaoCompleta.value = !visaoCompleta.value
}

// --- Ações de aprovação com auto-avanço ---
const confirmarReprovacao = () => {
  reprovarObservacao.value = ''
  reprovarModalRef.value?.show()
}

const confirmarAprovacao = () => {
  aprovarObservacao.value = ''
  if (confirmacaoAtiva.value) {
    aprovarModalRef.value?.show()
  } else {
    executarAprovacao(true)
  }
}

const avancarOuFecharAposAcao = () => {
  // Remove a OC atual da lista (foi aprovada/reprovada)
  lista.value.splice(indiceAtual.value, 1)

  if (totalPendentes.value === 0) {
    // Não há mais OCs pendentes — fecha o modal
    fecharModal()
    buscarDados(1)
    return
  }

  // Se o índice atual ultrapassou o tamanho, vai para a última
  if (indiceAtual.value >= totalPendentes.value) {
    indiceAtual.value = totalPendentes.value - 1
  }

  // Carrega a próxima OC (ou a anterior se estiver na última)
  carregarOCPorIndice(indiceAtual.value)
}

const executarAprovacao = async (confirmou) => {
  if (!confirmou || !idOrdemAnalise.value || estaProcessandoAcao.value) return
  estaProcessandoAcao.value = true

  const textareaAprovacao = document.getElementById('observacaoAprovacao')
  aprovarObservacao.value = textareaAprovacao ? textareaAprovacao.value.trim() : ''
  try {
    await api.post(`/OrdensCompra/${idOrdemAnalise.value}/aprovar`, {
      acao: 'APROVAR',
      observacao: aprovarObservacao.value || null
    })
    emit('show-notification', { message: 'Ordem de compra aprovada com sucesso.', type: 'success' })
    usePendenciasStore().atualizarContagem()
    avancarOuFecharAposAcao()
  } catch (error) {
    if (error.response?.status === 403) {
      emit('show-notification', { message: 'Você não tem permissão para aprovar esta ordem.', type: 'error' })
    } else {
      const msg = error.response?.data?.message || 'Erro ao aprovar a ordem de compra.'
      emit('show-notification', { message: msg, type: 'error' })
    }
  } finally {
    estaProcessandoAcao.value = false
  }
}

const executarReprovacao = async (confirmou) => {
  if (!confirmou || !idOrdemAnalise.value || estaProcessandoAcao.value) return
  estaProcessandoAcao.value = true

  const textareaReprovacao = document.getElementById('observacaoReprovacao')
  reprovarObservacao.value = textareaReprovacao ? textareaReprovacao.value.trim() : ''
  try {
    await api.post(`/OrdensCompra/${idOrdemAnalise.value}/reprovar`, {
      acao: 'REPROVAR',
      observacao: reprovarObservacao.value || null
    })
    emit('show-notification', { message: 'Ordem de compra reprovada.', type: 'success' })
    usePendenciasStore().atualizarContagem()
    avancarOuFecharAposAcao()
  } catch (error) {
    if (error.response?.status === 403) {
      emit('show-notification', { message: 'Você não tem permissão para reprovar esta ordem.', type: 'error' })
    } else {
      const msg = error.response?.data?.message || 'Erro ao reprovar a ordem de compra.'
      emit('show-notification', { message: msg, type: 'error' })
    }
  } finally {
    estaProcessandoAcao.value = false
  }
}

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({ buscarDados, abrirModalAnalise })
</script>

<template>
  <PaginaAdministrar
    titulo="Aprovações de Ordens de Compra"
    subtitulo="Análise e aprovação de ordens pendentes"
    :filtros-abertos="true"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="false"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @atualizar="aplicarFiltros"
  >
    <template #filtros>
      <!-- Filtro: Nº Ordem -->
      <div class="filtro-inline-row">
        <div class="filtro-group filtro-numero">
          <label class="form-label filtro-label">Nº Ordem</label>
          <input
            type="text"
            class="form-control form-control-sm"
            v-model="filtros.buscaNumero"
            placeholder="Nº da ordem..."
            @keydown.enter.prevent="aplicarFiltros"
          >
        </div>

        <!-- Filtro: Resumo -->
        <div class="filtro-group filtro-resumo">
          <label class="form-label filtro-label">Resumo</label>
          <input
            type="text"
            class="form-control form-control-sm"
            v-model="filtros.buscaResumo"
            placeholder="Filtrar por resumo..."
            @keydown.enter.prevent="aplicarFiltros"
          >
        </div>

        <!-- Filtro: Faixa de Valor -->
        <div class="filtro-group filtro-valor">
          <label class="form-label filtro-label">Valor</label>
          <div class="d-flex flex-wrap gap-1">
            <button
              v-for="faixa in faixasValor"
              :key="faixa.value"
              class="btn btn-sm"
              :class="filtros.faixaValor === faixa.value ? 'btn-primary' : 'btn-outline-secondary'"
              @click="alternarFaixaValor(faixa.value)"
            >
              {{ faixa.label }}
            </button>
          </div>
        </div>

        <!-- Filtro: Grupo Aprovador (badges) -->
        <div class="filtro-group filtro-grupo" v-if="gruposDisponiveis.length > 0">
          <label class="form-label filtro-label">Grupo</label>
          <div class="d-flex flex-wrap gap-1">
            <button
              v-for="grupo in gruposDisponiveis"
              :key="grupo.id"
              class="badge rounded-pill px-3 py-2 text-decoration-none border-0"
              :class="filtros.idGrupoAprovador === grupo.id ? 'text-bg-info' : 'text-bg-light text-body-secondary border'"
              @click="alternarGrupo(grupo.id)"
            >
              {{ grupo.nome }}
            </button>
          </div>
        </div>

        <!-- Botões de ação -->
        <div class="filtro-group filtro-acoes">
          <div class="d-flex gap-1">
            <button
              class="btn btn-sm btn-outline-secondary"
              @click="limparFiltros"
              :disabled="!temFiltroAtivo"
              title="Limpar filtros"
            >
              <i class="bi bi-x-lg"></i>
            </button>
            <button class="btn btn-sm btn-primary" @click="aplicarFiltros">
              <i class="bi bi-search"></i>
            </button>
          </div>
        </div>
      </div>
    </template>

    <div v-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <template v-else>
      <!-- Hint: duplo clique abre análise -->
      <div class="d-flex align-items-center gap-2 mb-2 text-body-secondary small">
        <i class="bi bi-info-circle"></i>
        <span>Duplo clique na linha para analisar a ordem</span>
      </div>

      <AdminTable
        :columns="colunas"
        :data="lista"
        :loading="loading"
        empty-message="Nenhuma ordem pendente de aprovação encontrada."
        row-key="idOrdemCompra"
        v-model:sort-field="sortField"
        v-model:sort-order="sortOrder"
        @sort-change="handleSortChange"
        @row-dblclick="({ row }) => abrirModalAnalise(row)"
      >
      <template #idOrdemCompra="{ row }">
        <span class="text-muted fw-medium">#{{ row.idOrdemCompra }}</span>
      </template>

      <template #dataEmissao="{ row }">
        <span class="small">{{ formatarDataBR(row.dataEmissao) }}</span>
      </template>

      <template #designadoDiretamente="{ row }">
        <span v-if="row.designadoDiretamente" class="badge text-bg-info">Direto</span>
        <span v-else class="text-muted">&mdash;</span>
      </template>

      <template #resumoAprovacao="{ row }">
        <span class="text-truncate d-inline-block" style="max-width: 200px;" :title="row.resumoAprovacao">
          {{ row.resumoAprovacao || '-' }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper">
          <button class="btn btn-sm btn-outline-info" @click="abrirModalAnalise(row)" title="Analisar Ordem">
            <i class="bi bi-search me-1"></i> Analisar
          </button>
        </div>
      </template>
    </AdminTable>
    </template>
  </PaginaAdministrar>

  <!-- Modal de Análise -->
  <ModalFormularioV2
    v-model="modalAberto"
    :titulo="'Análise da Ordem de Compra' + (posicaoLabel ? ' (' + posicaoLabel + ')' : '')"
    tamanho="xl"
    :cor-header="'var(--bs-info)'"
  >
    <template #header>
      <h5 class="modal-title-v2 mb-0 d-flex align-items-center gap-2">
        <i class="bi bi-file-earmark-text"></i>
        Análise da Ordem de Compra
        <span v-if="ordemDetalhes" class="badge bg-light text-info-emphasis fs-6">
          {{ ordemDetalhes.numeroOrdem }}
        </span>
        <span v-if="posicaoLabel" class="badge bg-info-subtle text-info-emphasis fs-6 ms-auto">
          {{ posicaoLabel }}
        </span>
      </h5>
    </template>

    <!-- Loading -->
    <div v-if="isLoadingDetalhes" class="text-center py-5">
      <div class="spinner-border text-info mb-3" role="status"></div>
      <p class="text-body-secondary">Carregando detalhes da ordem...</p>
    </div>

    <template v-else-if="ordemDetalhes">
      <!-- ===== VISÃO ENXUTA (padrão) ===== -->
      <template v-if="!visaoCompleta">
        <!-- Resumo em destaque (primeiro) -->
        <div class="bg-body-tertiary border rounded p-2 mb-3">
          <small class="text-body-secondary d-block lh-1 mb-1 fw-bold">
            <i class="bi bi-chat-quote me-1 text-info"></i> Resumo
          </small>
          <p class="mb-0 text-body-secondary small">{{ ordemDetalhes.resumoAprovacao || 'Nenhum resumo registrado.' }}</p>
          <small v-if="ordemDetalhes.nomeUsuarioCriacao" class="text-body-secondary d-block mt-1 small">
            <i class="bi bi-person-fill me-1 text-info"></i> Criado por: <span class="fw-medium">{{ ordemDetalhes.nomeUsuarioCriacao }}</span>
          </small>
        </div>

        <!-- Grid compacto de informações-chave -->
        <div class="row g-2 mb-3">
          <div class="col-6 col-md-3">
            <div class="border rounded p-2 h-100 bg-body-tertiary">
              <small class="text-body-secondary d-block lh-1 mb-1">Fornecedor</small>
              <span class="fw-medium text-body-emphasis small">{{ ordemDetalhes.nomeFornecedor || '-' }}</span>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="border rounded p-2 h-100 bg-body-tertiary">
              <small class="text-body-secondary d-block lh-1 mb-1">Valor Total</small>
              <span class="fw-medium text-body-emphasis small">{{ formatarMoedaComSigla(ordemDetalhes.valorTotalNegociado, ordemDetalhes.lstMoedaPadrao) }}</span>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="border rounded p-2 h-100 bg-body-tertiary">
              <small class="text-body-secondary d-block lh-1 mb-1">Status</small>
              <span class="badge badge-ghost-info">{{ getTextoLista(listas.LstStatusOrdemCompra, ordemDetalhes.lstStatusOrdemCompra) }}</span>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="border rounded p-2 h-100 bg-body-tertiary">
              <small class="text-body-secondary d-block lh-1 mb-1">Grupo Aprovador</small>
              <span class="fw-medium text-body-emphasis small">{{ ordemDetalhes.nomeGrupoAprovador || '-' }}</span>
            </div>
          </div>
        </div>

        <!-- Itens com scroll próprio -->
        <div class="mb-2">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <small class="fw-bold text-body-emphasis">
              <i class="bi bi-list-check me-1 text-info"></i> Itens ({{ ordemDetalhes.itens?.length || 0 }})
            </small>
          </div>
          <div v-if="ordemDetalhes.itens && ordemDetalhes.itens.length > 0" class="itens-scroll-wrapper border rounded bg-body-tertiary p-1">
            <div
              v-for="(item, idx) in ordemDetalhes.itens"
              :key="idx"
              class="d-flex flex-wrap align-items-center gap-1 px-2 py-1 border-bottom border-secondary-subtle item-row"
              :class="{ 'border-bottom-0': idx === ordemDetalhes.itens.length - 1 }"
            >
              <div class="fw-medium text-body-emphasis" style="min-width: 140px; flex: 1;">
                <span class="small" :title="item.nomeProduto">{{ item.nomeProduto || 'Produto #' + item.idProduto }}</span>
                <small v-if="item.sku" class="text-body-secondary d-block" style="font-size: 0.7rem;">{{ item.sku }}</small>
              </div>
              <div class="text-body-secondary small text-nowrap">
                {{ formatarValor(item.quantidadePedidaUnFornecedor) }} {{ getTextoLista(listas.LstUnidadeMedidaFornecedor, item.lstUnidadeMedidaFornecedor) }}
              </div>
              <div class="text-body-emphasis fw-medium small text-nowrap ms-auto">
                {{ formatarValor(item.valorTotalNegociado) }}
              </div>
            </div>
          </div>
          <div v-else class="text-center text-body-secondary small py-2">
            Nenhum item registrado.
          </div>
        </div>

        <!-- Botão Ver Mais Detalhes -->
        <div class="text-center mt-2">
          <button type="button" class="btn btn-outline-info btn-sm px-3" @click="alternarVisao">
            <i class="bi bi-chevron-expand me-1"></i> Ver Mais Detalhes
          </button>
        </div>
      </template>

      <!-- ===== VISÃO COMPLETA ===== -->
      <template v-else>
        <!-- Resumo em destaque (primeiro) -->
        <div class="bg-body-tertiary border rounded p-2 mb-3">
          <small class="text-body-secondary d-block lh-1 mb-1 fw-bold">
            <i class="bi bi-chat-quote me-1 text-info"></i> Resumo
          </small>
          <p class="mb-0 text-body-secondary small">{{ ordemDetalhes.resumoAprovacao || 'Nenhum resumo registrado.' }}</p>
          <small v-if="ordemDetalhes.nomeUsuarioCriacao" class="text-body-secondary d-block mt-1 small">
            <i class="bi bi-person-fill me-1 text-info"></i> Criado por: <span class="fw-medium">{{ ordemDetalhes.nomeUsuarioCriacao }}</span>
          </small>
        </div>

        <div class="row g-2 mb-3">
          <div class="col-md-3">
            <small class="text-body-secondary d-block lh-1 mb-1">Fornecedor</small>
            <span class="fw-medium small">{{ ordemDetalhes.nomeFornecedor || '-' }}</span>
          </div>
          <div class="col-md-3">
            <small class="text-body-secondary d-block lh-1 mb-1">Filial</small>
            <span class="small">{{ ordemDetalhes.nomeFilial || '-' }}</span>
          </div>
          <div class="col-md-3">
            <small class="text-body-secondary d-block lh-1 mb-1">Operação</small>
            <span class="small">{{ ordemDetalhes.nomeOperacao || '-' }}</span>
          </div>
          <div class="col-md-3">
            <small class="text-body-secondary d-block lh-1 mb-1">Status</small>
            <span class="badge badge-ghost-info">{{ getTextoLista(listas.LstStatusOrdemCompra, ordemDetalhes.lstStatusOrdemCompra) }}</span>
          </div>
          <div class="col-md-3">
            <small class="text-body-secondary d-block lh-1 mb-1">Data Emissão</small>
            <span class="small">{{ formatarDataBR(ordemDetalhes.dataEmissao) }}</span>
          </div>
          <div class="col-md-3">
            <small class="text-body-secondary d-block lh-1 mb-1">Previsão Entrega</small>
            <span class="small">{{ ordemDetalhes.dataPrevisaoEntrega ? formatarDataBR(ordemDetalhes.dataPrevisaoEntrega) : '-' }}</span>
          </div>
          <div class="col-md-3">
            <small class="text-body-secondary d-block lh-1 mb-1">Incoterm</small>
            <span class="small">{{ getTextoLista(listas.LstIncoterm, ordemDetalhes.lstIncoterm) }}</span>
          </div>
          <div class="col-md-3">
            <small class="text-body-secondary d-block lh-1 mb-1">Forma Pagamento</small>
            <span class="small">{{ getTextoLista(listas.LstFormaPagamento, ordemDetalhes.lstFormaPagamento) }}</span>
          </div>
          <div class="col-md-3">
            <small class="text-body-secondary d-block lh-1 mb-1">Condição Pagamento</small>
            <span class="small">{{ ordemDetalhes.idCondicaoPagamento ? (ordemDetalhes.idCondicaoPagamento + ' - ' + ordemDetalhes.nomeCondicaoPagamento) : '-' }}</span>
          </div>
          <div class="col-md-3">
            <small class="text-body-secondary d-block lh-1 mb-1">Valor Total</small>
            <span class="fw-medium small">{{ formatarMoedaComSigla(ordemDetalhes.valorTotalNegociado, ordemDetalhes.lstMoedaPadrao) }}</span>
          </div>
          <div class="col-md-3">
            <small class="text-body-secondary d-block lh-1 mb-1">Grupo Aprovador</small>
            <span class="small">{{ ordemDetalhes.nomeGrupoAprovador || '-' }}</span>
          </div>
        </div>

        <!-- Tabela completa de itens -->
        <div v-if="ordemDetalhes.itens && ordemDetalhes.itens.length > 0" class="mb-2">
          <small class="fw-bold text-body-emphasis d-block mb-1">
            <i class="bi bi-list-check me-1 text-info"></i> Itens ({{ ordemDetalhes.itens.length }})
          </small>
          <div class="table-responsive itens-scroll-wrapper">
            <table class="table table-sm table-hover align-middle mb-0">
              <thead class="table-light">
                <tr>
                  <th style="font-size: 0.75rem;">Produto</th>
                  <th style="font-size: 0.75rem;">Lote</th>
                  <th class="text-end" style="font-size: 0.75rem;">Qtd Forn</th>
                  <th class="text-center" style="font-size: 0.75rem;">Un</th>
                  <th class="text-end" style="font-size: 0.75rem;">Vlr Total</th>
                  <th style="font-size: 0.75rem;">Observação</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in ordemDetalhes.itens" :key="idx">
                  <td>
                    <span class="fw-medium text-body-emphasis small">{{ item.nomeProduto || 'Produto #' + item.idProduto }}</span>
                    <small v-if="item.sku" class="d-block text-body-secondary" style="font-size: 0.65rem;">{{ item.sku }}</small>
                  </td>
                  <td><span class="small">{{ item.numeroLoteFornecedor || '-' }}</span></td>
                  <td class="text-end fw-medium small">{{ formatarValor(item.quantidadePedidaUnFornecedor) }}</td>
                  <td class="text-center small">{{ getTextoLista(listas.LstUnidadeMedidaFornecedor, item.lstUnidadeMedidaFornecedor) }}</td>
                  <td class="text-end fw-medium small">{{ formatarValor(item.valorTotalNegociado) }}</td>
                  <td class="small text-body-secondary">{{ item.observacao || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="text-center text-body-secondary small py-2">
          Nenhum item registrado.
        </div>

        <!-- Botão Ver Menos -->
        <div class="text-center mt-2">
          <button type="button" class="btn btn-outline-secondary btn-sm px-3" @click="alternarVisao">
            <i class="bi bi-chevron-contract me-1"></i> Ver Menos
          </button>
        </div>
      </template>
    </template>

    <!-- Footer com navegação e ações -->
    <template #footer>
      <div class="d-flex flex-wrap gap-2 w-100 align-items-center">
        <!-- Navegação entre OCs -->
        <div class="d-flex gap-1">
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="anteriorOC" :disabled="!temAnterior || isLoadingDetalhes" title="Anterior">
            <i class="bi bi-chevron-left"></i>
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="proximaOC" :disabled="!temProxima || isLoadingDetalhes" title="Próximo">
            <i class="bi bi-chevron-right"></i>
          </button>
        </div>

        <span class="text-body-secondary small me-auto">{{ posicaoLabel }}</span>

        <div class="form-check form-check-inline mb-0">
          <input class="form-check-input" type="checkbox" id="confirmacaoAtiva" v-model="confirmacaoAtiva">
          <label class="form-check-label small text-body-secondary" for="confirmacaoAtiva">
            Habilitar Checagem de Aprovação
          </label>
        </div>

        <button type="button" class="btn btn-outline-danger btn-sm flex-fill flex-md-grow-0" @click="confirmarReprovacao" :disabled="isLoadingDetalhes || estaProcessandoAcao">
          <i class="bi bi-x-circle me-1"></i> Reprovar
        </button>
        <button type="button" class="btn btn-success btn-sm flex-fill flex-md-grow-0" @click="confirmarAprovacao" :disabled="isLoadingDetalhes || estaProcessandoAcao">
          <i class="bi bi-check-circle me-1"></i> Aprovar
        </button>
      </div>
    </template>
  </ModalFormularioV2>

  <!-- ConfirmationModal para Reprovar -->
  <ConfirmationModal
    ref="reprovarModalRef"
    title="Reprovar Ordem de Compra"
    :message="'Tem certeza que deseja <strong>reprovar</strong> esta ordem de compra?<br><br><textarea id=\'observacaoReprovacao\' class=\'form-control\' rows=\'3\' placeholder=\'Observação (opcional)...\' style=\'text-align: left; font-size: 0.9rem;\'></textarea>'"
    confirm-button-text="Sim, Reprovar"
    cancel-button-text="Cancelar"
    @confirmed="executarReprovacao"
  />

  <!-- ConfirmationModal para Aprovar -->
  <ConfirmationModal
    ref="aprovarModalRef"
    title="Aprovar Ordem de Compra"
    :message="'Tem certeza que deseja <strong>aprovar</strong> esta ordem de compra?<br><br><textarea id=\'observacaoAprovacao\' class=\'form-control\' rows=\'3\' placeholder=\'Observação (opcional)...\' style=\'text-align: left; font-size: 0.9rem;\'></textarea>'"
    confirm-button-text="Sim, Aprovar"
    cancel-button-text="Cancelar"
    @confirmed="executarAprovacao"
  />
</template>

<style scoped>
/* === Filtros inline - mobile-first === */
.filtro-inline-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: flex-end;
}

.filtro-group {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.filtro-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--bs-secondary-color);
  margin-bottom: 0;
}

.filtro-numero {
  flex: 1 1 140px;
  min-width: 120px;
}

.filtro-resumo {
  flex: 1 1 160px;
  min-width: 130px;
}

.filtro-valor {
  flex: 0 1 auto;
}

.filtro-grupo {
  flex: 1 1 100%;
}

.filtro-acoes {
  flex: 0 0 auto;
}

/* Badges de grupo clicáveis */
.filtro-grupo .badge {
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.15s ease;
  white-space: nowrap;
}

/* Badge não selecionada: fundo semi-transparente com borda para garantir contraste em dark/light */
.filtro-grupo .badge.text-bg-light {
  background-color: color-mix(in srgb, var(--bs-secondary-bg) 60%, transparent) !important;
  color: var(--bs-body-color) !important;
  border-color: var(--bs-border-color) !important;
}

.filtro-grupo .badge:hover {
  opacity: 0.85;
  transform: translateY(-1px);
}

/* Botões de faixa de valor */
.filtro-valor .btn {
  font-size: 0.72rem;
  padding: 0.2rem 0.5rem;
  white-space: nowrap;
}

/* Scroll itens */
.itens-scroll-wrapper {
  max-height: 220px;
  overflow-y: auto;
}

.itens-scroll-wrapper::-webkit-scrollbar {
  width: 6px;
}

.itens-scroll-wrapper::-webkit-scrollbar-thumb {
  background-color: var(--bs-border-color);
  border-radius: 3px;
}

.item-row:last-child {
  border-bottom: none !important;
}

/* Ajustes responsivos */
@media (max-width: 576px) {
  .filtro-inline-row {
    gap: 0.4rem;
  }

  .filtro-numero,
  .filtro-resumo {
    flex: 1 1 100%;
    min-width: 0;
  }

  .filtro-valor {
    flex: 1 1 100%;
  }

  .filtro-valor .btn {
    flex: 1;
    text-align: center;
  }

  .filtro-grupo .badge {
    font-size: 0.7rem;
    padding: 0.25rem 0.6rem;
  }

  .filtro-acoes {
    flex: 1 1 100%;
  }

  .filtro-acoes .d-flex {
    justify-content: flex-end;
  }
}
</style>
